import { motion } from 'framer-motion'
import { User, Settings, LogOut, Calendar, Clock, MapPin, Plus, ArrowRight, Mail, Star, Download, RefreshCw, Eye, Zap, Moon, Sun, Activity, Edit, Save, X, AlertCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useState, useEffect } from 'react'
import { userJourneyAPI } from '../services/userJourneyService'
import AstrologicalChartDisplay from '../components/AstrologicalChartDisplay'
import InteractiveBirthForm from '../components/InteractiveBirthForm'
import { useChartImport } from '../hooks/useChartImport'
import { config } from '../config/environment'
import { API_BASE_URL_WITH_VERSION } from '../config/api'

const Profile = () => {
  const { user, updateProfile } = useAuth()
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [isEditingBirthData, setIsEditingBirthData] = useState(false)
  const [email, setEmail] = useState('')
  const [birthData, setBirthData] = useState<any>(null)
  const [chartData, setChartData] = useState<any>(null)
  const [loadingChart, setLoadingChart] = useState(false)
  const [showBirthForm, setShowBirthForm] = useState(false)
  const [detailedChartData, setDetailedChartData] = useState<any>(null)
  const [loadingDetailedCharts, setLoadingDetailedCharts] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(false)

  // Chart import functionality
  const {
    setError: setChartError,
    isImporting,
    isComplete,
    charts,
    error: chartError,
    hasCompleteBirthData,
    triggerImport,
    resetImport
  } = useChartImport()

  // Load current user profile from backend
  const loadUserProfile = async () => {
    if (!user?.uid) return
    
    try {
      setLoadingProfile(true)
      const response = await userJourneyAPI.getUserProfile(user.uid)
      if (response.success && response.data?.profile) {
        setEmail(response.data.profile.email || '')
        setBirthData(response.data.profile.birthData || null)
      }
    } catch (error) {
      console.error('Error loading user profile:', error)
    } finally {
      setLoadingProfile(false)
    }
  }

  const loadChartData = async () => {
    if (!user?.uid) return
    
    try {
      setLoadingChart(true)
      const response = await userJourneyAPI.getUserProfile(user.uid)
      if (response.success && response.data?.charts) {
        // Set chart data from the charts object
        setChartData(response.data.charts)
      }
    } catch (error) {
      console.error('Error loading chart data:', error)
    } finally {
      setLoadingChart(false)
    }
  }

  const loadDetailedChartData = async () => {
    if (!user?.uid) return
    
    try {
      setLoadingDetailedCharts(true)
      // Use the user journey profile endpoint which returns charts in the correct format
      const response = await userJourneyAPI.getUserProfile(user.uid)
      if (response.success && response.data?.charts) {
        console.log('Chart data received:', response.data.charts)
        setDetailedChartData(response.data.charts)
      }
    } catch (error) {
      console.error('Error loading detailed chart data:', error)
    } finally {
      setLoadingDetailedCharts(false)
    }
  }

  useEffect(() => {
    if (user?.uid) {
      loadUserProfile()
      loadChartData()
      loadDetailedChartData()
    }
  }, [user?.uid])

  // Handle email update
  const handleEmailUpdate = async () => {
    if (!user?.uid) return
    
    try {
      const response = await updateProfile(user.uid, { email })
      if (response.success) {
        setIsEditingEmail(false)
        await loadUserProfile() // Refresh profile data
      }
    } catch (error) {
      console.error('Error updating email:', error)
    }
  }

  // Handle birth data update
  const handleBirthDataUpdate = async (newBirthData: any) => {
    if (!user?.uid) return
    
    try {
      const response = await updateProfile(user.uid, { birthData: newBirthData })
      if (response.success) {
        setIsEditingBirthData(false)
        await loadUserProfile() // Refresh profile data
      }
    } catch (error) {
      console.error('Error updating birth data:', error)
    }
  }

  // Check if user has complete charts
  const hasCompleteCharts = () => {
    return detailedChartData && Object.keys(detailedChartData).length >= 5
  }

  // Get missing chart types
  const getMissingChartTypes = () => {
    if (!detailedChartData) return ['astro_details', 'planets', 'horo_chart', 'current_vdasha', 'kalsarpa_details']
    
    const expectedCharts = ['astro_details', 'planets', 'horo_chart', 'current_vdasha', 'kalsarpa_details']
    const existingCharts = Object.keys(detailedChartData)
    return expectedCharts.filter(chart => !existingCharts.includes(chart))
  }

  // Get chart completion percentage
  const getChartCompletionPercentage = () => {
    if (!detailedChartData) return 0
    return Math.round((Object.keys(detailedChartData).length / 5) * 100)
  }

  // Check RAG system status
  const checkRAGStatus = async () => {
    if (!user?.uid) return
    
    try {
      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/rag/user-charts/${user.uid}`)
      if (response.ok) {
        const data = await response.json()
        console.log('RAG system status:', data)
        return data.success
      }
    } catch (error) {
      console.error('Error checking RAG status:', error)
    }
    return false
  }

  // Trigger RAG processing for user charts
  const triggerRAGProcessing = async () => {
    if (!user?.uid) return
    
    try {
      console.log('Triggering RAG processing for user charts...')
      
      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/auth/trigger-rag-processing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || 'dev_test_token_123'}`
        }
      })

      if (response.ok) {
        const result = await response.json()
        console.log('RAG processing successful:', result)
        alert(`✅ RAG processing completed! ${result.chartCount} charts processed.`)
      } else {
        const errorData = await response.json()
        console.error('RAG processing failed:', errorData)
        alert(`❌ RAG processing failed: ${errorData.error}`)
      }
    } catch (error) {
      console.error('Error triggering RAG processing:', error)
      alert('❌ Error triggering RAG processing')
    }
  }

  // Manual chart generation fallback
  const generateChartsManually = async () => {
    if (!user?.uid || !birthData) return
    
    try {
      setLoadingChart(true)
      setChartError('')
      
      // Call the backend to generate charts manually
      const response = await fetch(`${config.apiBaseUrl}/api/auth/generate-charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || 'dev_test_token_123'}`
        },
        body: JSON.stringify({
          userId: user.uid,
          birthData: birthData
        })
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          // Refresh chart data
          await loadDetailedChartData()
          // Show success message
          alert('Charts generated successfully! You are now online.')
        } else {
          setChartError(result.error || 'Failed to generate charts')
        }
      } else {
        setChartError('Failed to generate charts. Please try again.')
      }
    } catch (error) {
      console.error('Error generating charts manually:', error)
      setChartError('An error occurred while generating charts')
    } finally {
      setLoadingChart(false)
    }
  }

  // Store user data in RAG system when birth data is available
  const storeUserDataInRAG = async (userData: any) => {
    if (!user?.uid) return
    
    try {
      // Store user profile in RAG
      await fetch(`${config.apiBaseUrl}/api/rag/store-user-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          userData: {
            name: user.name,
            email: user.email,
            birthData: user.birthData,
            preferences: user.preferences || {}
          }
        })
      });

      // Chart data is now stored in Firestore during signup and processed by RAG at runtime
      // No need to manually store charts here anymore
      if (chartData) {
        console.log('Chart data available (stored in Firestore, processed by RAG at runtime):', { 
          chartTypes: Object.keys(chartData),
          message: 'Charts are automatically processed when needed for AI analysis'
        });
      } else {
        console.log('No chart data available');
      }

      console.log('User data stored in RAG system successfully');
    } catch (error) {
      console.error('Error storing user data in RAG:', error);
    }
  };

  // Store user data in RAG when birth data is available
  useEffect(() => {
    if (user?.birthData && user.uid) {
      storeUserDataInRAG(user);
    }
  }, [user?.birthData, user?.uid, chartData]);

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 className="mobile-text-large mb-4">
            <span className="gradient-text">Profile</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Manage your account and astrological charts
          </p>
        </motion.div>

        <div className="mobile-spacing">
          {/* User Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mobile-card"
          >
            <h2 className="mobile-text-medium text-white mb-6 flex items-center">
              <User className="w-6 h-6 mr-3" />
              User Information
            </h2>
            
            {user ? (
              <div className="mobile-spacing">
                <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-semibold text-lg">{user.name || 'User'}</div>
                    <div className="text-gray-400 text-sm">Active User</div>
                  </div>
                </div>
                
                {/* Email Field */}
                <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div className="flex-1">
                    {isEditingEmail ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                        />
                        <button
                          onClick={handleEmailUpdate}
                          className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEmail(user?.email || '')
                            setIsEditingEmail(false)
                          }}
                          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-gray-300 text-sm">Email</div>
                          <div className="text-white font-medium">
                            {email || user.email || 'No email provided'}
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditingEmail(true)}
                          className="px-3 py-1 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm flex items-center space-x-1"
                        >
                          <Edit className="w-3 h-3" />
                          <span>{email || user.email ? 'Edit' : 'Add'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {birthData && (
                  <div className="mobile-spacing">
                    <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl">
                      <Calendar className="w-5 h-5 text-purple-400" />
                      <div className="flex-1">
                        <div className="text-gray-300 text-sm">Birth Date</div>
                        <div className="text-white font-medium">
                          {birthData.birthDate}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <div className="flex-1">
                        <div className="text-gray-300 text-sm">Birth Time</div>
                        <div className="text-white font-medium">
                          {birthData.birthTime}
                        </div>
                      </div>
                    </div>
                    
                    {birthData.placeOfBirth && (
                      <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl">
                        <MapPin className="w-5 h-5 text-green-400" />
                        <div className="flex-1">
                          <div className="text-gray-300 text-sm">Place of Birth</div>
                          <div className="text-white font-medium">{birthData.placeOfBirth}</div>
                        </div>
                      </div>
                    )}
                    
                    <button
                      onClick={() => setShowBirthForm(true)}
                      className="w-full mt-4 mobile-button-secondary flex items-center justify-center space-x-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Edit Birth Details</span>
                    </button>
                  </div>
                )}
                
                {!birthData && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Plus className="w-8 h-8 text-purple-400" />
                    </div>
                    <p className="text-gray-300 mb-4">No birth data provided yet</p>
                    <button
                      onClick={() => setShowBirthForm(true)}
                      className="mobile-button-primary inline-flex items-center space-x-2"
                    >
                      <span>Add Birth Details</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Loading State */}
                {loadingProfile && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                    <p className="text-gray-300 mt-2">Loading profile data...</p>
                  </div>
                )}

                {/* Chart Status Display */}
                {detailedChartData && Object.keys(detailedChartData).length > 0 && (
                  <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl mb-4">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <div className="flex-1">
                      <div className="text-gray-300 text-sm">Astrological Charts Status</div>
                      <div className="text-white font-medium">
                        {Object.keys(detailedChartData).length}/5 Complete ({getChartCompletionPercentage()}%)
                      </div>
                      <div className="text-gray-400 text-xs mt-1">
                        {hasCompleteCharts() ? '✅ All charts generated successfully! You are ONLINE!' : '⏳ Charts are being generated...'}
                      </div>
                      {hasCompleteCharts() && (
                        <div className="text-green-400 text-xs mt-1">
                          🎉 Welcome to RRAASI! Your cosmic journey begins now.
                        </div>
                      )}
                      {!hasCompleteCharts() && (
                        <div className="text-orange-400 text-xs mt-1">
                          Missing: {getMissingChartTypes().join(', ')}
                        </div>
                      )}
                      {/* Progress Bar */}
                      <div className="mt-2">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${getChartCompletionPercentage()}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Offline Status Warning */}
                {hasCompleteBirthData() && (!detailedChartData || Object.keys(detailedChartData).length < 5) && (
                  <div className="flex items-center space-x-3 p-4 glass-effect rounded-xl mb-4 border border-red-500/30">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <div className="flex-1">
                      <div className="text-red-300 text-sm font-medium">⚠️ You are currently OFFLINE</div>
                      <div className="text-gray-300 text-xs mt-1">
                        Complete your astrological charts to unlock all RRAASI features and go online.
                      </div>
                    </div>
                  </div>
                )}

                {/* Astrological Charts Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mobile-card mt-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="mobile-text-medium text-white flex items-center">
                      <Star className="w-6 h-6 mr-3" />
                      Astrological Charts
                    </h2>
                    <div className="flex space-x-2">
                      {hasCompleteBirthData() && (
                        <button
                          onClick={triggerImport}
                          disabled={isImporting}
                          className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm disabled:opacity-50 flex items-center space-x-1"
                        >
                          {isImporting ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              <span>Importing...</span>
                            </>
                          ) : (
                            <>
                              <Download className="w-3 h-3" />
                              <span>Import Charts</span>
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={loadChartData}
                        disabled={loadingChart}
                        className="px-3 py-1 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm disabled:opacity-50"
                      >
                        {loadingChart ? 'Loading...' : 'Refresh'}
                      </button>
                    </div>
                  </div>

                  {/* Chart Import Status */}
                  {isImporting && (
                    <div className="mb-4 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
                        <span className="text-blue-300">Importing astrological charts...</span>
                      </div>
                    </div>
                  )}

                  {/* Manual Chart Generation Fallback */}
                  {!isImporting && hasCompleteBirthData() && (!detailedChartData || Object.keys(detailedChartData).length < 5) && (
                    <div className="mb-4 p-4 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="w-4 h-4 text-orange-400" />
                          <span className="text-orange-300">Astro Agents not active. Add birth details to activate your astrological team.</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={checkRAGStatus}
                            className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm flex items-center space-x-1"
                          >
                            <RefreshCw className="w-3 h-3" />
                            <span>Check RAG</span>
                          </button>
                          <button
                            onClick={triggerRAGProcessing}
                            className="px-3 py-1 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm flex items-center space-x-1"
                          >
                            <Zap className="w-3 h-3" />
                            <span>Process RAG</span>
                          </button>
                          <button
                            onClick={generateChartsManually}
                            disabled={loadingChart}
                            className="px-3 py-1 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm disabled:opacity-50 flex items-center space-x-1"
                          >
                            {loadingChart ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                <span>Generating...</span>
                              </>
                            ) : (
                              <>
                                <Zap className="w-3 h-3" />
                                <span>Generate Charts</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {chartError && (
                    <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <span className="text-red-300 text-sm">{chartError}</span>
                    </div>
                  )}

                  {isComplete && charts.length > 0 && (
                    <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-green-400" />
                        <span className="text-green-300">Successfully imported {charts.length} charts</span>
                      </div>
                    </div>
                  )}

                  {/* Chart Display */}
                  {(chartData || charts.length > 0) && (
                    <AstrologicalChartDisplay />
                  )}

                  {/* Detailed Chart Data Display */}
                  {detailedChartData && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="mt-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white flex items-center">
                          <Eye className="w-5 h-5 mr-2 text-purple-400" />
                          Detailed Chart Analysis
                        </h3>
                        <button
                          onClick={loadDetailedChartData}
                          disabled={loadingDetailedCharts}
                          className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm disabled:opacity-50 flex items-center space-x-1"
                        >
                          {loadingDetailedCharts ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              <span>Loading...</span>
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3 h-3" />
                              <span>Refresh</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* Loading State for Detailed Charts */}
                      {loadingDetailedCharts && (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
                          <p className="text-gray-300">Loading detailed astrological data...</p>
                        </div>
                      )}

                      {/* Error State for Detailed Charts */}
                      {!loadingDetailedCharts && detailedChartData && Object.keys(detailedChartData).length === 0 && (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8 text-red-400" />
                          </div>
                          <p className="text-gray-300 mb-4">Failed to load detailed chart data</p>
                          <button
                            onClick={loadDetailedChartData}
                            className="mobile-button-primary inline-flex items-center space-x-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span>Retry</span>
                          </button>
                        </div>
                      )}

                      {/* Chart Data Sections - Only render if data exists */}
                      {!loadingDetailedCharts && detailedChartData && Object.keys(detailedChartData).length > 0 && (
                        <>
                          {/* Debug Info */}
                          <div className="mb-4 p-3 bg-gray-800/50 rounded-lg text-xs text-gray-300">
                            <div>Chart Data Keys: {Object.keys(detailedChartData).join(', ')}</div>
                            <div>Astro Details: {detailedChartData.astro_details ? `${detailedChartData.astro_details.length} items` : 'undefined'}</div>
                            {detailedChartData.astro_details && detailedChartData.astro_details[0] && (
                              <div>Astro Details[0] Keys: {Object.keys(detailedChartData.astro_details[0]).join(', ')}</div>
                            )}
                          </div>
                          
                          {/* Fallback: Display raw chart data if structure is unexpected */}
                          {(!detailedChartData.astro_details || !detailedChartData.astro_details[0] || !detailedChartData.astro_details[0].data) && (
                            <div className="mb-6 p-4 glass-effect rounded-xl">
                              <div className="flex items-center mb-3">
                                <AlertCircle className="w-5 h-5 text-yellow-400 mr-2" />
                                <h4 className="text-white font-medium">Chart Data Available</h4>
                              </div>
                              <div className="text-sm text-gray-300">
                                <p>Chart data is available but in a different format than expected.</p>
                                <p className="mt-2">Available data types: {Object.keys(detailedChartData).join(', ')}</p>
                                <details className="mt-3">
                                  <summary className="cursor-pointer text-purple-400">View Raw Data</summary>
                                  <pre className="mt-2 p-2 bg-gray-800/50 rounded text-xs overflow-auto">
                                    {JSON.stringify(detailedChartData, null, 2)}
                                  </pre>
                                </details>
                              </div>
                            </div>
                          )}
                          
                          {/* Astro Details */}
                          {detailedChartData.astro_details && detailedChartData.astro_details[0] && detailedChartData.astro_details[0].data && (
                        <div className="mb-6 p-4 glass-effect rounded-xl">
                          <div className="flex items-center mb-3">
                            <Sun className="w-5 h-5 text-yellow-400 mr-2" />
                            <h4 className="text-white font-medium">Astrological Details</h4>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-400">Sign:</span>
                              <span className="text-white ml-2">{detailedChartData.astro_details[0].data.sign || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Ascendant:</span>
                              <span className="text-white ml-2">{detailedChartData.astro_details[0].data.ascendant || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Nakshatra:</span>
                              <span className="text-white ml-2">{detailedChartData.astro_details[0].data.Naksahtra || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Tithi:</span>
                              <span className="text-white ml-2">{detailedChartData.astro_details[0].data.Tithi || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Yog:</span>
                              <span className="text-white ml-2">{detailedChartData.astro_details[0].data.Yog || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Karan:</span>
                              <span className="text-white ml-2">{detailedChartData.astro_details[0].data.Karan || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Planetary Positions */}
                      {detailedChartData.planets && detailedChartData.planets[0] && detailedChartData.planets[0].data && (
                        <div className="mb-6 p-4 glass-effect rounded-xl">
                          <div className="flex items-center mb-3">
                            <Activity className="w-5 h-5 text-blue-400 mr-2" />
                            <h4 className="text-white font-medium">Planetary Positions</h4>
                          </div>
                          <div className="grid grid-cols-1 gap-2 text-sm">
                            {Object.values(detailedChartData.planets[0].data).filter((planet: any) => planet.name && planet.name !== 'Ascendant').map((planet: any, index: number) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-gray-800/50 rounded-lg">
                                <div className="flex items-center">
                                  <span className="text-purple-400 font-medium w-16">{planet.name || 'N/A'}</span>
                                  <span className="text-gray-400 mx-2">•</span>
                                  <span className="text-white">{planet.sign || 'N/A'}</span>
                                </div>
                                <div className="text-right">
                                  <div className="text-gray-400 text-xs">House {planet.house || 'N/A'}</div>
                                  <div className="text-xs text-gray-300">{planet.nakshatra || 'N/A'}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Horoscope Chart */}
                      {detailedChartData.horo_chart && detailedChartData.horo_chart[0] && detailedChartData.horo_chart[0].data && (
                        <div className="mb-6 p-4 glass-effect rounded-xl">
                          <div className="flex items-center mb-3">
                            <Zap className="w-5 h-5 text-green-400 mr-2" />
                            <h4 className="text-white font-medium">Horoscope Chart</h4>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            {Object.entries(detailedChartData.horo_chart[0].data).filter(([key, house]: [string, any]) => key !== 'type' && key !== 'userId').map(([houseNum, house]: [string, any]) => (
                              <div key={houseNum} className="p-2 bg-gray-800/50 rounded-lg text-center">
                                <div className="text-purple-400 font-medium">House {houseNum}</div>
                                <div className="text-white">{house.sign_name || 'N/A'}</div>
                                {house.planet && house.planet.length > 0 && (
                                  <div className="text-gray-400 text-xs mt-1">
                                    {house.planet.join(', ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Current Vimshottari Dasha */}
                      {detailedChartData.current_vdasha && detailedChartData.current_vdasha[0] && detailedChartData.current_vdasha[0].data && (
                        <div className="mb-6 p-4 glass-effect rounded-xl">
                          <div className="flex items-center mb-3">
                            <Moon className="w-5 h-5 text-indigo-400 mr-2" />
                            <h4 className="text-white font-medium">Current Vimshottari Dasha</h4>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Major Period:</span>
                              <span className="text-white">{detailedChartData.current_vdasha[0].data.major?.planet || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Minor Period:</span>
                              <span className="text-white">{detailedChartData.current_vdasha[0].data.minor?.planet || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Sub Period:</span>
                              <span className="text-white">{detailedChartData.current_vdasha[0].data.sub_minor?.planet || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Kaal Sarp Dosha */}
                      {detailedChartData.kalsarpa_details && detailedChartData.kalsarpa_details[0] && (
                        <div className="mb-6 p-4 glass-effect rounded-xl">
                          <div className="flex items-center mb-3">
                            <Zap className="w-5 h-5 text-red-400 mr-2" />
                            <h4 className="text-white font-medium">Kaal Sarp Dosha Analysis</h4>
                          </div>
                          <div className="space-y-3 text-sm">
                            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                              <div className="text-red-300 font-medium mb-2">
                                {detailedChartData.kalsarpa_details[0]?.data?.one_line || 'N/A'}
                              </div>
                              <div className="text-gray-300 text-xs leading-relaxed">
                                {detailedChartData.kalsarpa_details[0]?.data?.report?.report?.replace(/<[^>]*>/g, '') || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                        </>
                      )}
                    </motion.div>
                  )}

                  {!chartData && charts.length === 0 && !isImporting && hasCompleteBirthData() && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Star className="w-8 h-8 text-purple-400" />
                      </div>
                      <p className="text-gray-300 mb-4">No astrological charts imported yet</p>
                      <button
                        onClick={triggerImport}
                        className="mobile-button-primary inline-flex items-center space-x-2"
                      >
                        <Download className="w-4 h-4" />
                        <span>Import Charts</span>
                      </button>
                    </div>
                  )}

                  {!hasCompleteBirthData() && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Calendar className="w-8 h-8 text-purple-400" />
                      </div>
                      <p className="text-gray-300 mb-4">Complete your birth details to import charts</p>
                      <button
                        onClick={() => setShowBirthForm(true)}
                        className="mobile-button-primary inline-flex items-center space-x-2"
                      >
                        <span>Add Birth Details</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {loadingChart && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                      <p className="text-gray-300 mt-2">Loading astrological data...</p>
                    </div>
                  )}
                </motion.div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center mx-auto mb-6">
                  <img 
                    src="/rraasi_transparent.png" 
                    alt="Rraasi Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="mobile-text-medium text-white mb-4">
                  Welcome to rraasi
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Get started by providing your birth details for personalized insights
                </p>
                <button
                  onClick={() => setShowBirthForm(true)}
                  className="mobile-button-primary inline-flex items-center space-x-2"
                >
                  <span>Get Started</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>

          {/* Settings */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="mobile-card"
          >
            <h2 className="mobile-text-medium text-white mb-6 flex items-center">
              <Settings className="w-6 h-6 mr-3" />
              Settings
            </h2>
            
            <div className="mobile-spacing">
              <div className="flex items-center justify-between p-4 glass-effect rounded-xl">
                <div className="flex-1">
                  <h3 className="text-white font-medium">Notifications</h3>
                  <p className="text-gray-300 text-sm">Get updates about your readings</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-4 glass-effect rounded-xl">
                <div className="flex-1">
                  <h3 className="text-white font-medium">Data Privacy</h3>
                  <p className="text-gray-300 text-sm">Your data is stored locally and securely</p>
                </div>
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 glass-effect rounded-xl">
                <div className="flex-1">
                  <h3 className="text-white font-medium">ML Insights</h3>
                  <p className="text-gray-300 text-sm">Enable AI-powered recommendations</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Interactive Birth Form Modal */}
      {showBirthForm && (
        <InteractiveBirthForm
          onComplete={(birthData) => {
            setShowBirthForm(false)
            handleBirthDataUpdate(birthData)
          }}
          onCancel={() => setShowBirthForm(false)}
        />
      )}
    </div>
  )
}

export default Profile 